import React, { useEffect, useMemo, useState } from 'react';
import { BackTop, Button, Col, Form, Input, Modal, Popconfirm, Row, Select, Table, Typography, message } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { parseISO as _parseISO, format } from 'date-fns';
import { debounce } from 'lodash';

import Rest from 'tools/rest';
import Preloader from 'components/preloader';
import SiteDocumentCard from 'components/site_document_card';

const { Text } = Typography;

const SiteDocuments = () => {
  const [siteDocuments, setSiteDocuments] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per: 20, total: 0 });
  const [search, setSearch] = useState({ id: null, title: null, active: null });
  const [loading, setLoading] = useState(false);
  const [visibleCard, setVisibleCard] = useState(false);
  const [siteDocument, setSiteDocument] = useState(null);
  const [dataRelevance, setDataRelevance] = useState(null);

  const loadSiteDocuments = () => {
    setLoading(true);
    Rest.get('/api/v1/site_documents.json', {
      params: {
        page: meta.page,
        per: meta.per,
        search,
      },
    }).then((response) => {
      const { site_documents: loadedSiteDocuments, meta: loadedMeta } = response.data;
      setSiteDocuments(loadedSiteDocuments || []);
      setMeta((prev) => ({
        ...prev,
        page: loadedMeta?.page || prev.page,
        per: loadedMeta?.per || prev.per,
        total: loadedMeta?.total || 0,
      }));
    }).catch(() => message.error('Ошибка загрузки документов'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSiteDocuments();
  }, [meta.page, meta.per, search, dataRelevance]);

  const debouncedSearchChange = useMemo(() => debounce((name, value) => {
    setMeta((prev) => ({ ...prev, page: 1 }));
    setSearch((prev) => ({ ...prev, [name]: value }));
  }, 400), []);

  useEffect(() => () => debouncedSearchChange.cancel(), [debouncedSearchChange]);

  const handleDelete = (id) => {
    setLoading(true);
    Rest.delete(`/api/v1/site_documents/${id}.json`).then(() => {
      message.success('Документ удален');
      setDataRelevance(new Date());
      if (siteDocument?.id === id) {
        setVisibleCard(false);
        setSiteDocument(null);
      }
    }).catch(() => message.error('Ошибка удаления документа'))
      .finally(() => setLoading(false));
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Название', dataIndex: 'title', key: 'title' },
    { title: 'Позиция', dataIndex: 'position', key: 'position', width: 120 },
    {
      title: 'Статус',
      dataIndex: 'active',
      key: 'active',
      width: 140,
      render: (value) => (value ? 'Опубликован' : 'Скрыт'),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value) => (value ? format(_parseISO(value), 'dd.MM.yyyy HH:mm') : null),
    },
    {
      title: 'Файл',
      dataIndex: 'file_url',
      key: 'file_url',
      width: 120,
      render: (value) => (value ? <a href={value} target="_blank" rel="noreferrer">Открыть</a> : '-'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Удалить документ?"
          onConfirm={(event) => {
            event?.stopPropagation?.();
            handleDelete(record.id);
          }}
          onCancel={(event) => event?.stopPropagation?.()}
          okText="Удалить"
          cancelText="Отмена"
        >
          <Button danger size="small" onClick={(event) => event.stopPropagation()}>
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <React.Fragment>
      <BackTop />
      <PageHeader
        title="Реестр документов сайта"
        extra={[
          <Button
            key="new_site_document"
            type="primary"
            onClick={() => {
              setSiteDocument(null);
              setVisibleCard(true);
            }}
          >
            Добавить документ
          </Button>,
        ]}
      />
      <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="ID:" style={{ marginBottom: '5px' }}>
              <Input
                name="id"
                placeholder="Поиск по id"
                onChange={(e) => debouncedSearchChange('id', e.target.value || null)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Название:" style={{ marginBottom: '5px' }}>
              <Input
                name="title"
                placeholder="Поиск по названию"
                onChange={(e) => debouncedSearchChange('title', e.target.value || null)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Статус:" style={{ marginBottom: '5px' }}>
              <Select
                allowClear
                value={search.active}
                placeholder="Любой"
                onChange={(value) => {
                  setMeta((prev) => ({ ...prev, page: 1 }));
                  setSearch((prev) => ({ ...prev, active: value ?? null }));
                }}
                options={[
                  { label: 'Опубликован', value: 'true' },
                  { label: 'Скрыт', value: 'false' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Preloader loading={loading}>
        <Row gutter={20}>
          <Col style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
            <Text>Общее кол-во: {meta.total} шт.</Text>
          </Col>
        </Row>
        <Table
          style={{ marginTop: '10px' }}
          rowKey={(record) => record.id}
          loading={loading}
          columns={columns}
          dataSource={siteDocuments}
          onChange={(pagination) => {
            setMeta((prev) => ({
              ...prev,
              page: pagination.current,
              per: pagination.pageSize,
            }));
          }}
          pagination={{
            current: meta.page,
            pageSize: meta.per,
            total: meta.total,
            position: ['bottomCenter'],
            showSizeChanger: true,
          }}
          onRow={(record) => ({
            onClick: () => {
              setSiteDocument(record);
              setVisibleCard(true);
            },
          })}
        />
      </Preloader>
      {visibleCard && (
        <Modal
          title={siteDocument?.id ? `Документ № ${siteDocument.id}` : 'Новый документ'}
          visible={visibleCard}
          onCancel={() => setVisibleCard(false)}
          footer={false}
          width="70%"
        >
          <SiteDocumentCard
            siteDocumentId={siteDocument?.id}
            onUpdated={() => {
              setVisibleCard(false);
              setSiteDocument(null);
              setDataRelevance(new Date());
            }}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

export default SiteDocuments;
